import type {
  FleetCompanion,
  FleetDevice,
  FleetDeviceComplianceStatus,
  FleetIntegration,
  FleetMemberRow,
} from 'src/api/endpoints/cloudEnterprise.types';
import { deviceHelpers } from 'src/utils/deviceHelpers';
import { formatShortDate } from 'src/utils/formatDate';

import { getIntegrationName } from '../utils/getIntegrationName';
import { fleetUserHelpers } from './fleetUserHelpers';

const isUser = (row: FleetMemberRow) => row.type === 'user';
const isInvitation = (row: FleetMemberRow) => row.type === 'invitation';

const getEmail = (row: FleetMemberRow) => row.data.email ?? '';

const hasName = (row: FleetMemberRow) => {
  if (isInvitation(row)) return false;

  return fleetUserHelpers.hasName(row.data);
};

const getName = (row: FleetMemberRow) => {
  if (isInvitation(row)) return '';

  return fleetUserHelpers.getName(row.data);
};

const getRoles = (row: FleetMemberRow) => {
  if (isInvitation(row)) return [];

  return row.data.roles ?? [];
};

const getRoleNames = (row: FleetMemberRow) =>
  getRoles(row).map((role) => role.name);

const getIntegrations = (row: FleetMemberRow) =>
  isUser(row) ? (row.data.integrations ?? []) : [];

const getIntegrationNames = (row: FleetMemberRow) =>
  getIntegrations(row).map(getIntegrationName);

const getDevices = (row: FleetMemberRow) =>
  isUser(row) ? (row.data.devices ?? []) : [];

const getCompanions = (row: FleetMemberRow) =>
  isUser(row) ? (row.data.companions ?? []) : [];

const getAccountIssues = (row: FleetMemberRow) => {
  if (isInvitation(row)) return [];

  return (
    row.data.issues?.map((issue) => issue.title ?? '').filter(Boolean) ?? []
  );
};

const getIntegrationIssue = (integration: FleetIntegration) => {
  if (!integration.hasIssue) return '';

  const date = formatShortDate(integration.createdAt, {
    showTime: true,
    showYear: true,
  });

  const integrationName = getIntegrationName(integration);

  return `${
    integrationName ?? 'Unknown'
  } integration (${date}) needs reauthentication.`;
};

const getIntegrationIssues = (row: FleetMemberRow) =>
  getIntegrations(row).map(getIntegrationIssue).filter(Boolean) ?? [];

const getIssues = (row: FleetMemberRow) => [
  ...getAccountIssues(row),
  ...getIntegrationIssues(row),
];

const getDeviceLastSeenDate = (device: FleetDevice) =>
  device.lastSeen ? new Date(device.lastSeen) : null;

const getIntegrationLastSeenDate = (integration: FleetIntegration) =>
  integration.createdAt ? new Date(integration.createdAt) : null;

const getCompanionAppLastSeenDate = (companion: FleetCompanion) =>
  companion.lastSeen ? new Date(companion.lastSeen) : null;

const getLastSeenDate = (row: FleetMemberRow) => {
  if (isInvitation(row)) return;

  const deviceDates = row.data.devices?.map(getDeviceLastSeenDate) ?? [];

  const companionDates =
    row.data.companions?.map(getCompanionAppLastSeenDate) ?? [];

  const integrationDates =
    row.data.integrations?.map(getIntegrationLastSeenDate) ?? [];

  const allDates = [...deviceDates, ...companionDates, ...integrationDates]
    .filter(Boolean)
    .sort((a, b) => {
      if (!a || !b) return 0;
      return b.getTime() - a.getTime(); // Descending order
    }) as Date[];

  return allDates[0];
};

const getRowLabel = (row: FleetMemberRow) => {
  if (isInvitation(row)) {
    return `Invitation to ${getEmail(row)}`;
  }

  return [getName(row) || getEmail(row), getRoleNames(row).join(', ')]
    .filter(Boolean)
    .join(' - ');
};

const getBetaStatus = (row: FleetMemberRow) => {
  if (isUser(row)) {
    return row.data.betaStatus?.enrolled ? 'Yes' : 'No';
  }
  return 'No';
};

const shouldHighlightDevice = (
  globalFilter: string | undefined,
  devicesFilter: string[] | undefined,
  device: FleetDevice
) => {
  const globalFilterMatches =
    deviceHelpers.isStringMatchingDevice(globalFilter, device) ||
    (globalFilter && device.softwareVersion?.startsWith(globalFilter));

  const deviceFilterMatches =
    devicesFilter?.includes(device.serialNumber ?? '') ||
    devicesFilter?.includes(deviceHelpers.getDeviceName(device));

  if (globalFilter && devicesFilter && devicesFilter?.length > 0) {
    return !!globalFilterMatches && !!deviceFilterMatches;
  }

  return !!globalFilterMatches || !!deviceFilterMatches;
};

const getStatus = (row: FleetMemberRow) => {
  if (isInvitation(row)) return 'Invited';
  return fleetUserHelpers.getUserStatusLabel(row.data);
};

const isMdmDeactivated = (row: FleetMemberRow) =>
  isUser(row) && !!row.data.mdmDisabled;

const getDeviceComplianceStatus = (
  device: FleetDevice
): FleetDeviceComplianceStatus | undefined => device.policyCompliance?.status;

const isDeviceNonCompliant = (device: FleetDevice) =>
  getDeviceComplianceStatus(device) === 'nonCompliant';

export const fleetMemberRowHelpers = {
  getAccountIssues,
  getBetaStatus,
  getCompanionAppLastSeenDate,
  getCompanions,
  getDeviceComplianceStatus,
  getDeviceLastSeenDate,
  getDevices,
  getEmail,
  getIntegrationIssue,
  getIntegrationIssues,
  getIntegrationLastSeenDate,
  getIntegrationNames,
  getIntegrations,
  getIssues,
  getLastSeenDate,
  getName,
  getRoleNames,
  getRoles,
  getRowLabel,
  hasName,
  isDeviceNonCompliant,
  isInvitation,
  isMdmDeactivated,
  isUser,
  shouldHighlightDevice,
  getStatus,
};
